import React, { useState } from "react";
import { toast } from "sonner";
import { Upload, Loader2, FileSpreadsheet } from "lucide-react";
import { api, BRL, formatDateBR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const guessMonth = (name, fallback) => {
  const m = name.match(/(\d{4})-(\d{2})-\d{2}/);
  return m ? `${m[1]}-${m[2]}` : fallback;
};

export const CardImportButton = ({ currentMonth, onDone }) => {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [month, setMonth] = useState(currentMonth);
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  const pick = (f) => { setFile(f); setRes(null); if (f) setMonth(guessMonth(f.name, currentMonth)); };
  const send = async () => {
    const fd = new FormData();
    fd.append("month", month);
    fd.append("file", file);
    setBusy(true);
    try { const r = await api.post("/finance/cartao/import-csv", fd); setRes(r.data); toast.success("Extrato importado"); onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro ao importar"); }
    finally { setBusy(false); }
  };
  return (
    <>
      <Button variant="outline" onClick={() => { setOpen(true); setFile(null); setRes(null); setMonth(currentMonth); }} className="h-10 rounded-xl border-indigo-200 text-indigo-700 font-black" data-testid="card-import-open">
        <Upload className="w-4 h-4 mr-1.5" /> Importar extrato (CSV)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto overflow-x-hidden w-[calc(100vw-1.5rem)] sm:w-full grid-cols-1 [&>*]:min-w-0" data-testid="card-import-dialog">
          <DialogHeader><DialogTitle className="font-heading font-black text-indigo-800">Importar extrato do cartão</DialogTitle></DialogHeader>
          <p className="text-xs text-slate-500">No app do Nubank: Fatura → exportar CSV. O sistema atualiza o valor real da fatura e as parcelas de compras antigas. Seus lançamentos não são alterados.</p>
          <label className="mt-1 flex items-center gap-3 rounded-xl border-2 border-dashed border-indigo-200 p-4 cursor-pointer hover:bg-indigo-50/50" data-testid="card-import-file-label">
            <FileSpreadsheet className="w-6 h-6 text-indigo-500 shrink-0" />
            <span className="text-sm font-semibold text-slate-700 truncate">{file ? file.name : "Escolher arquivo .csv"}</span>
            <input type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => pick(e.target.files?.[0] || null)} data-testid="card-import-file" />
          </label>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Fatura de (mês do vencimento)</label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-10" data-testid="card-import-month" />
          </div>
          <Button onClick={send} disabled={!file || !month || busy} className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-black" data-testid="card-import-send">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Importar"}
          </Button>
          {res && (
            <div className="rounded-xl bg-slate-50 border border-slate-200 p-3 text-xs space-y-1.5" data-testid="card-import-result">
              <p><b>Valor real da fatura:</b> {BRL(res.bank_total)}</p>
              <p><b>Parcelas de compras antigas:</b> {BRL(res.legacy_this_month)} neste mês • {res.legacy_items} parcela(s) incluindo as próximas faturas</p>
              {res.not_in_system.length > 0 && (
                <div>
                  <p className="font-bold text-amber-700 mt-1">Compras novas que não achei no sistema ({res.not_in_system.length}):</p>
                  <ul className="mt-1 space-y-0.5">
                    {res.not_in_system.map((r, i) => (
                      <li key={i} className="flex gap-2"><span className="text-slate-400 w-16 shrink-0">{formatDateBR(r.date)}</span><span className="flex-1 min-w-0 truncate">{r.title}</span><b className="tabular-nums">{BRL(r.value)}</b></li>
                    ))}
                  </ul>
                  <p className="text-slate-500 mt-1">Lance essas compras nos orçamentos para elas entrarem na fatura.</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
